import { Color, Orientation } from "../common";
import { BehaviorData } from "../behaviordata";

/** Type identifier for Image components */
export const IMAGE_TYPE = "Image" as const;

/**
 * Controls image display settings
 */
export interface ImageData extends BehaviorData {
    "@type": typeof IMAGE_TYPE;

    /**
     * Image aspect ratio (width/height).
     *
     * Absent means UNKNOWN, not square. It used to default to 1, so every
     * image node was born claiming to be square — a claim made before anything
     * had looked at the image, yet strong enough to letterbox a landscape node
     * down to a square plane when the texture never arrived. Matches Video,
     * where the field has always been optional and undefined means "nobody has
     * measured this yet".
     * @step 0.01
     */
    aspect?: number;

    /**
     * Image orientation
     * @default "Vertical"
     */
    orientation?: Orientation;

    /**
     * How the image meets a box whose aspect differs from its own.
     *
     * `contain` (the default) letterboxes: the plane shrinks on one axis so
     * the whole image stays visible inside the box, never distorted.
     *
     * `cover` fills: the plane spans the box exactly and a centered window
     * of the image is shown instead, cropping the overflowing axis. The
     * crop is a view, not an edit — switching back to `contain` shows the
     * full image again.
     *
     * Absent means `contain`: exactly the fit scenes have always had.
     * @default "contain"
     */
    fit?: 'contain' | 'cover';

    /**
     * Corner radius in meters (the unit MeshBackground's cornerRadius
     * uses; measured here on the CURRENT visible media rectangle - a
     * media quad has no rest box). One value gives differently sized
     * frames identical corners, and a resized frame keeps its corner
     * size. Never larger than half the rectangle's current smaller side
     * - an oversized value degrades to a pill instead of self-crossing.
     *
     * Rounds the media quad itself, which neither Bevel (its host gate
     * takes only builtin primitives) nor MeshBackground (drives the
     * library rigs' authored morphs) can reach. Composes with `fit`: the
     * corners round the letterboxed rectangle under `contain`, the full
     * box under `cover`. Strictly geometry: paint stays on Renderer,
     * sizing stays on the transform; clients without a corner-radius
     * implementation render the sharp rectangle - content intact.
     * @default 0
     * @min 0
     * @step 0.001
     */
    cornerRadius?: number;

    /**
     * Round the quad to half its smaller side — a capsule at any size,
     * which is the one shape an absolute radius cannot name. Overrides
     * `cornerRadius` when true. Same vocabulary as Bevel and
     * MeshBackground (#759).
     * @default false
     */
    pill?: boolean;

    /**
     * What the quad's back shows. `none` culls it: nothing is drawn behind
     * the picture. `picture` renders the media on both faces of the one
     * quad - seen from behind it reads mirrored, as a print does through
     * its paper (a two-sided quad; glTF's doubleSided idea, NOT a second
     * readable surface). `color` backs the picture with a flat unlit
     * `backColor` card that also shows through transparent pixels from the
     * front. A GLB export carries what the object shows: `picture` as the
     * doubled quad, `color` as a second mesh named `<quad>_back`.
     * @default "none"
     */
    back?: 'none' | 'picture' | 'color';

    /**
     * The card color behind the picture when `back` is `color`
     * (#RRGGBB or #RRGGBBAA - the alpha rides the string). Ignored for
     * the other values.
     * @default "#FFFFFF"
     */
    backColor?: Color;
}
